import {Injectable, Inject, PLATFORM_ID} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {isPlatformBrowser, isPlatformServer} from '@angular/common';

import {MessageService} from './message.service';

/** Type of the handleError function returned by HttpErrorHandler.createHandleError */
export type HandleError = <T> (operation?: string, result?: T) => (error: HttpErrorResponse) => Observable<T>;

/** Handles HttpClient errors */
@Injectable({providedIn: 'root'})
export class HttpErrorHandler {
  constructor(
    private messageService: MessageService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
  }

  /** Create curried handleError function that already knows the service name */
  public createHandleError = (serviceName = '') => <T>(operation = 'operation', result = {} as T) => this.handleError(serviceName, operation, result);

  /**
   * Returns a function that handles Http operation failures.
   * This error handler lets the app continue to run as if no error occurred.
   * @param serviceName = name of the data service that attempted the operation
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  handleError<T>(serviceName = '', operation = 'operation', result = {} as T) {
    return (error: HttpErrorResponse): Observable<T> => {
      // 服务端渲染时，只记录错误但不中断流程
      if (isPlatformServer(this.platformId)) {
        console.log(`Server-side rendering error in ${serviceName}:`, {
          operation,
          url: error.url,
          status: error.status,
          message: error.message
        });
        return of(result);
      }

      // 浏览器环境下的错误处理
      console.error(error);
      let message: string;

      try {
        // 在 try-catch 块中处理可能的类型错误
        if (error.error instanceof ErrorEvent) {
          message = error.error.message;
        } else if (error.error && typeof error.error === 'object' && 'message' in error.error) {
          // 处理后端返回的错误对象
          message = error.error.message;
        } else if (typeof error.error === 'string') {
          // 处理字符串类型的错误
          message = error.error;
        } else {
          // 默认错误信息
          message = `server returned code ${error.status} with body "${JSON.stringify(error.error)}"`;
        }
      } catch (e) {
        // 如果出现类型错误，使用备用消息
        message = `Error: ${error.message || '未知错误'}`;
      }

      // 避免消息过长
      if (message && message.length > 200) {
        message = message.substring(0, 200) + '...';
      }

      this.messageService.add(`${serviceName}: ${operation} failed: ${message}`);
      return of(result);
    };
  }
}
